import {
  CompareSide,
  SceneLayerEntry,
  StoryContent,
  StoryLayerKey,
  StoryScene,
} from "../../types/storyMap";
import { type Bbox, bboxIntersects } from "../projectListing/projectBbox";

/**
 * Normalisation between the two layer-state shapes:
 *
 * v2 — `layerStack: SceneLayerEntry[]`, written by the "Add layer to map" UI;
 * each entry carries its own configuration (strategy, regime, simulation…).
 *
 * v1 (legacy) — a `layers: StoryLayerKey[]` array plus scene/side-level
 * config fields shared by every layer. Still read (old drafts and published
 * stories), never written by the builder any more.
 */

export const LAYER_KEY_LABELS: Record<StoryLayerKey, string> = {
  boundary: "Project boundary",
  properties: "Property boundaries",
  treatments: "Treatments",
  assets: "Assets",
  fuel_load: "Fuel load",
  fuel_3d: "3D fuel",
  vegetation: "Vegetation",
  fire_history: "Fire history",
  ignitions: "Ignitions",
  custom_layers: "Custom layers",
  risk_from: "Risk from",
  risk_to: "Risk to",
  inspection: "Inspection",
  freehand: "Freehand drawing",
  weather: "Weather",
};

/** Menu order for "Add layer to map". */
export const ADDABLE_LAYER_KEYS: StoryLayerKey[] = [
  "boundary",
  "properties",
  "treatments",
  "assets",
  "fuel_load",
  "fuel_3d",
  "vegetation",
  "fire_history",
  "ignitions",
  "custom_layers",
  "risk_from",
  "risk_to",
  "inspection",
  "freehand",
  "weather",
];

/** Keys whose entries render from a simulation's collated results. */
export const SIM_ENTRY_KEYS: StoryLayerKey[] = [
  "risk_from",
  "risk_to",
  "inspection",
  "weather",
];

/** Keys whose entries need (strategy, date)-resolved fuel/veg tiles. */
export const TILE_ENTRY_KEYS: StoryLayerKey[] = ["fuel_load", "fuel_3d", "vegetation"];

export function newLayerEntry(key: StoryLayerKey): SceneLayerEntry {
  if (key === "freehand") {
    return {
      id: crypto.randomUUID(),
      key,
      features: { type: "FeatureCollection", features: [] },
    };
  }
  return { id: crypto.randomUUID(), key };
}

/** Legend / chip label: the author's override, else the key's default. */
export function entryLabel(entry: SceneLayerEntry): string {
  const custom = entry.label?.trim();
  return custom || LAYER_KEY_LABELS[entry.key] || entry.key;
}

function legacyEntries(
  keys: string[] | undefined,
  shared: Omit<SceneLayerEntry, "id" | "key">,
  idPrefix: string
): SceneLayerEntry[] {
  if (!Array.isArray(keys)) return [];
  return keys.map((key) => ({
    // Deterministic ids so the freeze and the viewer derive the same ones.
    id: `${idPrefix}-${key}`,
    key: key as StoryLayerKey,
    ...shared,
  }));
}

/** A scene's configured layers, whichever shape the content carries. */
export function sceneLayerEntries(
  scene: StoryScene | undefined | null
): SceneLayerEntry[] {
  if (!scene) return [];
  if (Array.isArray(scene.layerStack)) return scene.layerStack;
  return legacyEntries(
    scene.layers,
    {
      strategyId: scene.strategyId,
      date: scene.date,
      tiles: scene.tiles,
      tilesStale: scene.tilesStale,
      treatmentTypes: scene.treatmentTypes,
      ignitionRegimeId: scene.ignitionRegimeId,
      customLayerIds: scene.customLayerIds,
      simulationId: scene.simulationId,
      riskFromMetric: scene.riskFromMetric,
      inspectionPoint: scene.inspectionPoint,
    },
    scene.id
  );
}

/** A compare side's configured layers, whichever shape the content carries. */
export function sideLayerEntries(
  side: CompareSide | undefined | null,
  sideId: string
): SceneLayerEntry[] {
  if (!side) return [];
  if (Array.isArray(side.layerStack)) return side.layerStack;
  return legacyEntries(
    side.layers,
    {
      strategyId: side.strategyId,
      date: side.date,
      tiles: side.tiles,
      tilesStale: side.tilesStale,
    },
    sideId
  );
}

// ---------------------------------------------------------------------------
// Scope: which project an entry draws from.
// ---------------------------------------------------------------------------

/** Keys that belong to no project (everything else is project-backed). */
export const PROJECT_FREE_KEYS: StoryLayerKey[] = ["freehand"];

/**
 * The project an entry, animation, metric source or image block cites: its
 * own `projectId`, else the story's project (project stories only). Null in a
 * folder or organisation-wide story means the author has not picked one yet.
 */
export function entryProjectId(
  obj: { projectId?: number | null } | null | undefined,
  defaultProjectId: number | null
): number | null {
  const raw = obj?.projectId;
  if (raw !== null && raw !== undefined) return Number(raw);
  return defaultProjectId;
}

/** Every project id a story's content cites (plus the story's own, if any). */
export function referencedProjectIds(
  content: StoryContent | null | undefined,
  defaultProjectId: number | null
): number[] {
  const ids = new Set<number>();
  if (defaultProjectId !== null) ids.add(defaultProjectId);
  const add = (obj: { projectId?: number | null } | null | undefined) => {
    const pid = obj?.projectId;
    if (pid !== null && pid !== undefined) ids.add(Number(pid));
  };
  (content?.scenes ?? []).forEach((scene) => {
    sceneLayerEntries(scene).forEach(add);
    (["before", "after"] as const).forEach((sideId) => {
      sideLayerEntries(scene.compare?.[sideId], sideId).forEach(add);
    });
    (scene.animations ?? []).forEach(add);
    (scene.blocks ?? []).forEach((block) => {
      if (block.type === "image") add(block);
      if (block.type === "metric" && block.source.kind === "sim_kpi") add(block.source);
      if (block.type === "metrics_row") {
        block.items.forEach((item) => {
          if (item.source.kind === "sim_kpi") add(item.source);
        });
      }
    });
  });
  return Array.from(ids);
}

/**
 * Opaque layers: where two projects' tiles overlap, the entry higher in the
 * list hides the other beneath it.
 */
export const OPAQUE_LAYER_KEYS: StoryLayerKey[] = ["fuel_load", "vegetation", "assets"];

/**
 * Entries whose project's extent overlaps another entry of the same opaque
 * kind from a different project: entry id → the other projects' names.
 */
export function overlapWarnings(
  entries: SceneLayerEntry[],
  projects: ReadonlyMap<number, { name: string; bbox: Bbox | null }>,
  defaultProjectId: number | null
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  entries.forEach((entry) => {
    if (!OPAQUE_LAYER_KEYS.includes(entry.key)) return;
    const pid = entryProjectId(entry, defaultProjectId);
    const own = pid !== null ? projects.get(pid) : undefined;
    if (!own?.bbox) return;
    const others = new Set<string>();
    entries.forEach((other) => {
      if (other.id === entry.id || other.key !== entry.key) return;
      const opid = entryProjectId(other, defaultProjectId);
      if (opid === null || opid === pid) return;
      const theirs = projects.get(opid);
      if (theirs?.bbox && bboxIntersects(own.bbox as Bbox, theirs.bbox)) others.add(theirs.name);
    });
    if (others.size) out.set(entry.id, Array.from(others));
  });
  return out;
}
